import { useCallback, useEffect, useRef, useState } from 'react';
import { getErrorMessage } from '../api/client';
import { getWasteReviewQueue, markWasteReviewed } from '../api/waste';
import type { WasteReviewItem, WasteReviewQueue, WasteReviewStatus } from '../types/waste';
import { defaultRange, formatWasteDateTime, localDateString, MAX_WASTE_RANGE_DAYS, rangeProblem, reviewedText } from '../utils/wasteReport';
import { formatWasteQuantity, wasteFlagBadges, wasteMeasureText } from '../utils/wasteReview';
import { PeriodField } from './period/PeriodPicker';
import { Icon } from './ui/Icon';
import { StatusBadge } from './ui/StatusBadge';
import { LoadingPanel } from './ui/primitives';

// F2 · «Por revisar»: las mermas MARCADAS (grande o consumo del personal) de un rango que el gerente
// todavía no revisa. «Ya la revisé» queda guardado (quién y cuándo); anular sigue siendo la reversa de F1.

const STATUS_OPTIONS: Array<[WasteReviewStatus, string]> = [['PENDING', 'Por revisar'], ['REVIEWED', 'Ya revisadas'], ['ALL', 'Todas']];

export function WasteReviewPanel({ stores, multiStore, refreshKey, timeZone, timeZoneOf, categoryOf, onReverse, onChanged }: {
  stores: Array<{ id: string; name: string }>;
  multiStore: boolean;
  /** Cambia cuando algo fuera del panel (anular) movió la lista. */
  refreshKey: number;
  /** Zona de la tienda para las fechas por defecto (no la del teléfono). */
  timeZone: string;
  timeZoneOf: (storeId: string) => string;
  categoryOf: (item: WasteReviewItem) => string;
  onReverse: (item: WasteReviewItem) => void;
  /** Avisa que el panel cambió algo (revisó una merma) para que «Hoy» se vuelva a leer. */
  onChanged: () => void;
}) {
  const [range, setRange] = useState(() => defaultRange(7, new Date(), timeZone));
  const [storeId, setStoreId] = useState('');
  const [status, setStatus] = useState<WasteReviewStatus>('PENDING');
  const [queue, setQueue] = useState<WasteReviewQueue>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busyId, setBusyId] = useState<string>();
  // Solo la respuesta de la ÚLTIMA carga se pinta (cambiar filtros rápido no mezcla resultados).
  const loadSeq = useRef(0);
  const problem = rangeProblem(range.dateFrom, range.dateTo);

  const load = useCallback(async () => {
    if (problem) return;
    const current = ++loadSeq.current;
    setLoading(true);
    setError(undefined);
    try {
      const result = await getWasteReviewQueue({ dateFrom: range.dateFrom, dateTo: range.dateTo, storeId: storeId || undefined, status });
      if (current === loadSeq.current) setQueue(result);
    } catch (loadError) {
      if (current === loadSeq.current) setError(getErrorMessage(loadError, 'No fue posible cargar las mermas por revisar.'));
    } finally {
      if (current === loadSeq.current) setLoading(false);
    }
  }, [problem, range.dateFrom, range.dateTo, storeId, status]);

  useEffect(() => {
    void load();
    return () => { loadSeq.current += 1; };
  }, [load, refreshKey]);

  const markReviewed = async (item: WasteReviewItem) => {
    if (busyId) return;
    setBusyId(item.id);
    setError(undefined);
    setNotice(undefined);
    try {
      await markWasteReviewed(item.id);
      setNotice(`Marcada como revisada: ${formatWasteQuantity(item.quantity, item.unitSymbol)} de ${item.ingredientName}.`);
      onChanged();
      await load();
    } catch (reviewError) {
      setError(getErrorMessage(reviewError, 'No fue posible marcarla como revisada.'));
    } finally {
      setBusyId(undefined);
    }
  };

  return (
    <section className="content-card" aria-label="Por revisar">
      <div className="content-card__header">
        <div>
          <span className="eyebrow">Revisión</span>
          <h2>Por revisar</h2>
          <p className="wiz-hint">
            Las mermas grandes y las de consumo del personal.
            {queue ? ` ${queue.counts.pending} por revisar · ${queue.counts.reviewed} ya revisada(s).` : ''}
            {queue?.partial ? ' Cifras parciales: estas fechas tienen más de 2,000 mermas.' : ''}
          </p>
        </div>
      </div>
      <div className="stock-filters">
        {/* P-075: el selector global de periodos en vez de dos calendarios (Desde / Hasta). Siempre pide un
            rango (sin «Todas las fechas»), con el «hoy» de la tienda y el mismo tope que el servidor
            (MAX_WASTE_RANGE_DAYS días entre extremos = uno más contándolos a los dos). Se aplica al elegir. */}
        <PeriodField
          allowAll={false}
          allowFuture
          className="date-range"
          label="Fechas"
          maxDays={MAX_WASTE_RANGE_DAYS + 1}
          onChange={(next) => setRange({ dateFrom: next.from, dateTo: next.to })}
          today={localDateString(new Date(), timeZone)}
          value={{ from: range.dateFrom, to: range.dateTo }}
        />
        {multiStore && (
          <label className="field field--inline"><span>Tienda</span>
            <select onChange={(event) => setStoreId(event.target.value)} value={storeId}>
              <option value="">Todas mis tiendas</option>
              {stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
            </select>
          </label>
        )}
        <label className="field field--inline"><span>Mostrar</span>
          <select onChange={(event) => setStatus(event.target.value as WasteReviewStatus)} value={status}>
            {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
      </div>
      {problem && <p className="wiz-hint" role="alert">{problem}</p>}
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {notice && <div className="alert alert--success" role="status">{notice}</div>}
      {loading && (queue ? <div className="inline-loading"><span className="spinner" />Actualizando…</div> : <LoadingPanel label="Cargando mermas por revisar…" />)}
      {queue && (queue.items.length === 0
        ? <p className="wiz-hint">{status === 'PENDING' ? 'No hay mermas por revisar en estas fechas.' : status === 'REVIEWED' ? 'No hay mermas revisadas en estas fechas.' : 'No hay mermas marcadas en estas fechas.'}</p>
        : <ul className="pending-rows">
          {queue.items.map((item) => (
            <li key={item.id}>
              <div className="pending-row pending-row--static waste-row">
                <span className={`pending-row__ic${item.reversed ? ' pending-row__ic--muted' : ' pending-row__ic--danger'}`}><Icon name={item.reversed ? 'trash' : 'warning'} size={18} /></span>
                <span className="pending-row__body">
                  <strong>{formatWasteQuantity(item.quantity, item.unitSymbol)} de {item.ingredientName}</strong>
                  <span>{formatWasteDateTime(item.occurredAt, timeZoneOf(item.storeId))} · {categoryOf(item)} · {item.createdBy?.name ?? 'Sin usuario'}{multiStore ? ` · ${item.storeName}` : ''}</span>
                  <span>{wasteMeasureText(item)}</span>
                  {item.notes && <span>Nota: {item.notes}</span>}
                  {item.flags.length > 0 && (
                    <span className="waste-row__flags">{wasteFlagBadges(item.flags).map((badge) => <StatusBadge key={badge.flag} variant={badge.variant}>{badge.label}</StatusBadge>)}</span>
                  )}
                  {item.pendingVoidRequest && !item.reversed && <span><StatusBadge variant="warning">Quien la registró pidió anularla</StatusBadge></span>}
                  {item.review
                    ? <span>{reviewedText(item.review, timeZoneOf(item.storeId))}</span>
                    : !item.reversed && <span><button className="link-button" disabled={busyId === item.id} onClick={() => void markReviewed(item)} type="button">{busyId === item.id ? 'Guardando…' : 'Ya la revisé'}</button></span>}
                </span>
                {item.reversed
                  ? <StatusBadge variant="neutral">Anulada</StatusBadge>
                  : item.reversible === false
                    // P-078 (BK28, Codex R2 · M4): la insignia y, visible, por qué no se anula aquí.
                    ? <span className="waste-lock"><StatusBadge variant="neutral">{item.lockReason === 'COUNTED_AFTER' ? 'Ya contada' : 'Del conteo'}</StatusBadge><small className="waste-lock__why">{item.lockReason === 'COUNTED_AFTER' ? 'Un conteo posterior ya la tomó en cuenta; para corregir, vuelve a contar.' : 'Se reconoció al contar; no se anula aquí.'}</small></span>
                    : <button className="button button--secondary" onClick={() => onReverse(item)} type="button">Anular</button>}
              </div>
            </li>
          ))}
        </ul>)}
      {queue?.truncated && <p className="wiz-hint">Se muestran 200. Acorta las fechas para ver las demás.</p>}
    </section>
  );
}
